import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Card } from '@stripe/stripe-js';
import { lastValueFrom } from 'rxjs';
import { TENANT_CONTEXT } from '../network-interceptor';
import { AppConfigService } from '../providers/config.service';
import { StateService } from './state.service';

@Injectable({
  providedIn: 'root',
})
export class CardService {
  private config = inject(AppConfigService);
  private http = inject(HttpClient);
  private state = inject(StateService);

  public async loadCard(): Promise<Card> {
    if (!['admin', 'owner'].includes(this.state.activeTenantRole)) return;
    const resp = await lastValueFrom(
      this.http.get<{ data: { id: string; card: Card; created: number }[] }>(
        `${this.config.apiEndpoint}/billing/cards`,
        {
          context: new HttpContext().set(TENANT_CONTEXT, this.state.activeTenant?.id),
        }
      )
    );
    const card = resp.data[0]?.card;
    this.state.card = card;
    this.state.card$.next(card);
    this.state.paymentMethod = resp.data[0]?.id;
    this.state.cardCreatedAt = resp.data[0]?.created;
    if (card) {
      this.state.cardExpired = this.isExpired(card);
      this.state.cardExpiringSoon = this.expiresSoon(card);
    } else {
      this.state.cardExpired = false;
      this.state.cardExpiringSoon = false;
    }
    return card;
  }

  // Don't call this without first making sure the customer doesn't have an active subscription
  public async deletePaymentMethod() {
    await lastValueFrom(this.http.delete(`${this.config.apiEndpoint}/billing/cards`, {}));
    this.state.card = undefined;
    this.state.card$.next(undefined);
    this.state.cardExpired = false;
    this.state.cardExpiringSoon = false;
  }

  public isExpired(card: Card = this.state.card): boolean {
    if (!card) return false;
    const date = new Date(card.exp_year, card.exp_month); // 0th  day of the next month because new Date month is 0 indexed
    return date.getTime() < Date.now();
  }

  public expiresSoon(card: Card = this.state.card): boolean {
    if (!card) return false;
    const date = new Date(card.exp_year, card.exp_month);
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    const days = diff / (1000 * 60 * 60 * 24);
    return days < 30;
  }
}
